
import React, { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Lead } from "./types";
import { 
  ResponsiveContainer, 
  PieChart, 
  Pie, 
  Cell, 
  Legend, 
  Tooltip 
} from "recharts";
import { Badge } from "@/components/ui/badge";

interface LeadSourceAnalyticsProps {
  leads: Lead[];
}

export const LeadSourceAnalytics: React.FC<LeadSourceAnalyticsProps> = ({ leads }) => {
  const sourceData = useMemo(() => {
    // Group leads by source
    const sources: Record<string, number> = {};
    
    leads.forEach(lead => {
      const source = lead.source || 'Unknown';
      if (!sources[source]) {
        sources[source] = 0;
      }
      sources[source]++;
    });
    
    // Convert to array for chart
    return Object.entries(sources).map(([name, value], index) => ({
      name,
      value,
      color: CHART_COLORS[index % CHART_COLORS.length]
    }));
  }, [leads]);

  // Calculate total and distribution
  const total = leads.length;
  
  if (total === 0) {
    return (
      <Card className="h-full">
        <CardHeader>
          <CardTitle>Lead Sources</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-[200px]">
          <p className="text-muted-foreground">No lead source data available</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle>Lead Sources</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={sourceData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {sourceData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [`${value} leads`, 'Count']} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
          
          <div className="space-y-2 self-center">
            <h4 className="text-sm font-medium">Distribution</h4>
            <div className="space-y-2">
              {sourceData.map((source) => (
                <div key={source.name} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div 
                      className="w-3 h-3 rounded-full mr-2"
                      style={{ backgroundColor: source.color }}
                    />
                    <span className="text-sm">{source.name}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge variant="outline" className="text-xs">
                      {source.value} leads
                    </Badge>
                    <Badge variant="secondary" className="text-xs">
                      {Math.round((source.value / total) * 100)}%
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Chart colors
const CHART_COLORS = [
  '#3b82f6', // blue
  '#10b981', // green
  '#f97316', // orange
  '#8b5cf6', // purple
  '#f43f5e', // rose
  '#6366f1', // indigo
  '#14b8a6', // teal
  '#eab308', // yellow
  '#8B5A2B', // brown
  '#64748b', // slate
];
